import { html, LitElement, type PropertyValues, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";

import structure from "../../../../bible-reader-data/dist/structure.web.json";
import { getQuery, getQueryPathForBookChapter } from "../query";
import { type QuerySuggestion, type QuerySuggestions } from "../query.worker";
import { ariaBoolean } from "..";
import { MatchesMediaController } from "./controllers/matches-media";
import css from "./bible-navigation-query-button.css?inline";

const structureBooks = structure.sections.flatMap((section) => section.books);

type StructureBook = (typeof structureBooks)[0];
type StructureBookOverview = StructureBook["overviews"][0];
type StructureSection = (typeof structure)["sections"][0];

@customElement("bible-navigation-query-button")
export class NavigationQueryButtonElement extends LitElement {
  static styles = unsafeCSS(css);

  private disconnectedCallbacks: (() => void)[] = [];

  @state()
  private expandedBooks = new Set<StructureBook>();

  private initialBook = getQuery().book.name;

  private initialChapter = getQuery().chapter;

  private isDesktopController = new MatchesMediaController(
    this,
    "desktop_dropdown",
  );

  private get isDesktop() {
    return this.isDesktopController.matches;
  }

  @property()
  isExpanded = false;

  private refs = {
    input: createRef<HTMLInputElement>(),
    button: createRef<HTMLButtonElement>(),
  };

  @state()
  private selectedBook: StructureBook | null = null;

  @state()
  private suggestions: QuerySuggestion[] | null = null;

  private suggestionsWorker = new Worker(
    new URL("../query.worker.ts", import.meta.url),
    {
      type: "module",
    },
  );

  private close() {
    this.isExpanded = false;
    this.updateComplete.then(() => {
      this.refs.button.value?.focus();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.withCleanup(() => {
      const handler = (e: MessageEvent<QuerySuggestions>) => {
        this.suggestions = e.data.suggestions;
        if (e.data.suggestions.length === 1) {
          const suggestion = e.data.suggestions[0];
          const book = structureBooks.find(
            ({ name }) => name === suggestion.book,
          );
          if (book) this.selectedBook = book;
        } else {
          this.selectedBook = null;
        }
      };
      this.suggestionsWorker.addEventListener("message", handler);
      return () => {
        this.suggestionsWorker.removeEventListener("message", handler);
        this.suggestionsWorker.terminate();
      };
    });
    this.withCleanup(() => {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "k" && e.metaKey) {
          this.isExpanded = !this.isExpanded;
        }
      };
      document.body.addEventListener("keydown", handler);
      return () => {
        document.body.removeEventListener("keydown", handler);
      };
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    while (this.disconnectedCallbacks.length > 0)
      this.disconnectedCallbacks.shift()!();
  }

  private focusFirstChapter() {
    this.shadowRoot!.querySelector(".chapters_list")
      ?.firstElementChild // @ts-ignore
      ?.focus();
  }

  private onButtonClick() {
    this.isExpanded = !this.isExpanded;
  }

  private onButtonKeyUp(e: KeyboardEvent) {
    if (e.key === "ArrowDown" && !this.isExpanded) {
      this.isExpanded = true;
    }
  }

  private onKeyUp(e: KeyboardEvent) {
    if (e.key === "Escape") {
      this.close();
    }
  }

  private onInput() {
    const $input = this.refs.input.value!;
    const query = $input.value;
    if (!query) {
      this.selectedBook = null;
      this.suggestions = null;
      $input.value = "";
      return;
    }
    this.suggestionsWorker.postMessage({ query });
  }

  private onInputKeyUp(e: KeyboardEvent) {
    if (e.key === "Enter") {
      const [suggestion] = this.suggestions ?? [];
      if (suggestion) {
        this.isExpanded = false;
      }
    }
  }

  render() {
    if (this.isDesktop) {
      return html`
        ${this.isExpanded
          ? html`
              <div @keyup=${this.onKeyUp}>
                ${this.renderInput()}
                <!-- -->
                ${this.renderPicker()}
              </div>
            `
          : this.renderButton()}
      `;
    }
    return html`
      <div @keyup=${this.onKeyUp}>
        ${this.renderButton()}
        <!-- -->
        ${this.isExpanded ? this.renderPicker() : null}
      </div>
    `;
  }

  private renderButton() {
    return html`
      <button
        ${ref(this.refs.button)}
        @click=${this.onButtonClick}
        @keyup=${this.onButtonKeyUp}
        part="button trigger trigger--full"
      >
        ${this.initialBook} ${this.initialChapter}
      </button>
    `;
  }

  private renderInput() {
    return html`
      <input
        ${ref(this.refs.input)}
        @input=${this.onInput}
        @keyup=${this.onInputKeyUp}
        aria-controls="picker"
        aria-expanded=${ariaBoolean(this.isExpanded)}
        aria-haspopup="true"
        part="trigger trigger--full"
        placeholder="Type a book and a chapter"
      />
    `;
  }

  private renderPicker() {
    if (this.isDesktop) {
      return html`
        <bible-navigation-menu .trigger=${this} part="bible-navigation-menu">
          <div class="picker" id="picker">
            <div class="books" part="books">
              <div
                aria-hidden=${ariaBoolean(this.suggestions !== null)}
                class="books_list"
                part="books-list"
              >
                ${repeat(
                  structure.sections,
                  (section) => section.name,
                  (section) => this.renderPickerBookSection(section),
                )}
              </div>
              ${this.renderPickerSuggestions()}
            </div>
            <div class="chapters" part="chapters">
              ${this.selectedBook
                ? this.renderPickerBookChaptersTemplate(this.selectedBook)
                : html`<bible-video-feature
                    description="The Bible is the most influential book in human history, but what is it? This video series will help you understand the Bible and see its unified message."
                    image-url="https://d1bsmz3sdihplr.cloudfront.net/media/explore-v2/How%20to%20Read%20the%20Bible/Introduction/Introduction%20Series/intro_rich.jpg"
                    .video=${{
                      muxId: "SPNOVb3wBYm9x027k91yQrXN4RzVoufBU6fVB47Q9nI4",
                      title: "How to Read the Bible",
                    }}
                    part="bible-video-feature"
                  ></bible-video-feature>`}
            </div>
          </div>
        </bible-navigation-menu>
      `;
    }
    return html`
      <bible-navigation-menu .trigger=${this} part="bible-video-feature">
        <div class="picker" id="picker" part="picker">
          <div class="picker__header" part="picker-header">
            <div class="picker__header__title" part="header-title">
              Choose a Book
            </div>
            <button
              @click=${this.close}
              class="picker__header__close"
              part="picker-header-close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.125 17.5781C21.5469 18.0469 21.5469 18.75 21.125 19.1719C20.6562 19.6406 19.9531 19.6406 19.5312 19.1719L14 13.5938L8.42188 19.1719C7.95312 19.6406 7.25 19.6406 6.82812 19.1719C6.35938 18.75 6.35938 18.0469 6.82812 17.5781L12.4062 12L6.82812 6.42188C6.35938 5.95312 6.35938 5.25 6.82812 4.82812C7.25 4.35938 7.95312 4.35938 8.375 4.82812L14 10.4531L19.5781 4.875C20 4.40625 20.7031 4.40625 21.125 4.875C21.5938 5.29688 21.5938 6 21.125 6.46875L15.5469 12L21.125 17.5781Z"
                  fill="#1B1B1B"
                />
              </svg>
            </button>
          </div>
          <!-- Tabs -->
          <div part="picker-tabs">
            <div part="picker-tab-1">Traditional</div>
            <div part="picker-tab-2">Hebrew</div>
            <div part="picker-tab-3">Storyline</div>
          </div>
          <!-- /Tabs -->
          <div class="books" part="books">
            <div class="books_list" part="books-list">
              ${repeat(
                structure.sections,
                (section) => section.name,
                (section) => this.renderPickerBookSection(section),
              )}
            </div>
          </div>
        </div>
      </bible-navigation-menu>
    `;
  }

  private renderPickerBookSection(section: StructureSection) {
    if (this.isDesktop) {
      const createClickHandler = (book: StructureBook) => (e: MouseEvent) => {
        this.selectedBook = book;
        // Only focus the first chapter if the button was clicked by the keyboard
        if (e.detail === 0) {
          this.focusFirstChapter();
        }
      };
      return html`
        <div class="books_list__section" part="books-list-section">
          <h3
            class="books_list__section__title"
            part="books-list-section-title"
          >
            ${section.name}
          </h3>
          <div
            class="books_list__section__books"
            part="books-list-section-books"
          >
            ${repeat(
              section.books,
              (book) => book.name,
              (book) => html`
                <button @click=${createClickHandler(book)}>${book.name}</button>
              `,
            )}
          </div>
        </div>
      `;
    }
    const createClickHandler = (book: StructureBook) => (_: MouseEvent) => {
      const expandedBooks = new Set(this.expandedBooks);
      if (this.expandedBooks.has(book)) {
        expandedBooks.delete(book);
      } else {
        expandedBooks.add(book);
      }
      this.expandedBooks = expandedBooks;
    };
    return html`
      <div class="books_list__section" part="books-list-section">
        <h3 class="books_list__section__title" part="books-list-section-title">
          ${section.name}
        </h3>
        <div class="books_list__section__books" part="books-list-section-books">
          ${repeat(
            section.books,
            (book) => book.name,
            (book) => {
              const isExpanded = this.expandedBooks.has(book);
              return html`
                <button
                  @click=${createClickHandler(book)}
                  aria-expanded=${ariaBoolean(isExpanded)}
                  aria-controls=${`books-list-chapters-${book.slug}`}
                  id=${`books-list-button-${book.slug}`}
                  part="books-list-section-book"
                >
                  ${book.name}
                </button>
                ${isExpanded
                  ? html`<div
                      aria-describedby=${`books-list-button-${book.slug}`}
                      class="books_list__section__book"
                      id=${`books-list-chapters-${book.slug}`}
                      part="books-list-section-book-expanded"
                    >
                      ${this.renderPickerBookChaptersTemplate(book)}
                    </div>`
                  : null}
              `;
            },
          )}
        </div>
      </div>
    `;
  }

  private renderPickerBookChaptersTemplate(book: StructureBook) {
    return html`
      <div class="overviews_list" part="overviews-list">
        ${book.overviews.map((overview) => this.renderBookOverview(overview))}
      </div>
      <div class="chapters_list" part="chapters-list">
        ${repeat(
          Array.from({ length: book.chapters }, (_, i) => i + 1),
          (chapter) => chapter,
          (chapter) => html`
            <a
              aria-label="Chapter ${chapter}"
              part="chapters-list-item"
              href=${getQueryPathForBookChapter(book.slug, chapter)}
              >${chapter}</a
            >
          `,
        )}
      </div>
    `;
  }

  private renderBookOverview(overview: StructureBookOverview) {
    return html`
      <div class="overview" part="overview">
        <div
          class="overview_image"
          part="overview-image"
          style="${`background-image: url('${overview.images.small}')`}"
        >
          <bible-video-play-button
            size="small"
            part="play-button"
            .video=${{
              muxId: overview.mux_id,
              title: overview.title,
            }}
          ></bible-video-play-button>
        </div>
        <div class="overview_pretitle" part="overview-pretitle">
          Book Overview
        </div>
        <div class="overview_title" part="overview-title">
          ${overview.title}
        </div>
        <div class="overview_subtitle" part="overview-subtitle">
          ${overview.subtitle}
        </div>
      </div>
    `;
  }

  private renderPickerSuggestions() {
    const createClickHandler = (item: QuerySuggestion) => (e: MouseEvent) => {
      if (this.isDesktop) {
        this.selectedBook =
          structureBooks.find(({ name }) => name === item.book) ?? null;
        if (e.detail === 0) {
          this.focusFirstChapter();
        }
      }
    };
    return this.suggestions
      ? html` <div class="books_list" part="books-list">
          <div
            class="books_list__section__books"
            part="books-list-section-books"
          >
            ${repeat(
              this.suggestions,
              (suggestion) => suggestion.book,
              (suggestion) =>
                html`<button
                  @click=${createClickHandler(suggestion)}
                  aria-selected=${ariaBoolean(
                    suggestion.book === this.selectedBook?.name,
                  )}
                >
                  ${suggestion.book}
                </button>`,
            )}
          </div>
        </div>`
      : null;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has("isExpanded") && this.isExpanded) {
      this.dispatchEvent(new CustomEvent("open"));
      this.refs.input.value?.focus();
    }
  }

  private withCleanup(fn: () => () => void) {
    this.disconnectedCallbacks.push(fn());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-navigation-query-button": NavigationQueryButtonElement;
  }
}
